import { Injectable, signal } from '@angular/core';
import { QueryConfig } from '@wonder-bi-front/query-engine/interfaces/query.interface';
import { ApplyChartConfigEvent } from '../ai-store/ai-chat.types';

/** Agent that works on each screen: the query builder's or the chart builder's. */
export type AssistantAgentMode = 'query' | 'chart';

/**
 * What a builder screen lends the global assistant while it is open (WB-155): the config it is
 * editing, so the agent answers about THAT config, and the way to put a generated result into it.
 * The assistant's chat lives in the header bar, so a screen is the only thing that can do either.
 */
export interface AssistantScreen {
  mode: AssistantAgentMode;
  currentConfig: () => Record<string, unknown> | null;
  applyQueryConfig?: (queryConfig: QueryConfig) => void;
  applyChartConfig?: (event: ApplyChartConfigEvent) => void;
  /** Chart screen: opens a query the agent proposed in a dialog, to validate and save it. */
  openQueryConfigDialog?: (queryConfig: QueryConfig) => void;
}

type PendingAction = { mode: AssistantAgentMode; run: (screen: AssistantScreen) => void };

/** The builder screen currently open, if any, as seen by the global assistant. */
@Injectable({ providedIn: 'root' })
export class AssistantScreenService {
  private readonly _screen = signal<AssistantScreen | null>(null);
  readonly screen = this._screen.asReadonly();
  private pending: PendingAction | null = null;

  /** Registers the open screen; returns the call that unregisters it, to run on destroy. */
  register(screen: AssistantScreen): () => void {
    this._screen.set(screen);
    const pending = this.pending;
    if (pending?.mode === screen.mode) {
      this.pending = null;
      pending.run(screen);
    }
    // A screen being destroyed after its successor registered must not unregister the successor.
    return () => {
      if (this._screen() === screen) this._screen.set(null);
    };
  }

  /**
   * Runs `run` against the screen of `mode`: now when it is open, or as soon as it opens. Only the
   * latest waiting action is kept — a newer request replaces an older one that never got its screen.
   */
  whenScreen(mode: AssistantAgentMode, run: (screen: AssistantScreen) => void): void {
    const screen = this._screen();
    if (screen?.mode === mode) {
      run(screen);
      return;
    }
    this.pending = { mode, run };
  }

  /** Drops the action waiting for its screen: the move that was going to open it was cancelled. */
  cancelPending(): void {
    this.pending = null;
  }
}
